/**
 * 运维健康看板 — 服务状态、僵死批量任务检测与释放
 */

import { authFetch } from './api.js';
import { showToast } from './utils.js';
import { registerViewLoader } from './navigation.js';

let lastHealth = null;

function createHealthElement(tagName, className = '', text = undefined) {
  const element = document.createElement(tagName);
  if (className) element.className = className;
  if (text !== undefined && text !== null) element.textContent = String(text);
  return element;
}

function replaceHealthChildren(element, ...children) {
  if (element) element.replaceChildren(...children);
  return element;
}

function createHealthBadge(tone = '', text = '') {
  const className = tone ? `badge badge-${tone}` : 'badge';
  return createHealthElement('span', className, text);
}

function createHealthEmptyTableRow(colspan, message, icon = '') {
  const row = createHealthElement('tr');
  const cell = createHealthElement('td');
  cell.colSpan = colspan;
  const state = createHealthElement('div', 'empty-state');
  if (icon) state.append(createHealthElement('div', 'empty-icon', icon));
  state.append(createHealthElement('p', '', message));
  cell.append(state);
  row.append(cell);
  return row;
}

export function initSystemHealth() {
  registerViewLoader('admin-system-health', () => loadSystemHealth());

  document.getElementById('health-refresh-btn')?.addEventListener('click', () => loadSystemHealth());
  document.getElementById('health-reconcile-btn')?.addEventListener('click', () => reconcileStuckJobs(false));
  document.getElementById('health-dryrun-btn')?.addEventListener('click', () => reconcileStuckJobs(true));
  document.getElementById('registry-update-check-btn')?.addEventListener('click', () => checkRegistryUpdate());
}

export async function loadSystemHealth() {
  const statusEl = document.getElementById('health-overall-status');
  const gridEl = document.getElementById('health-stats-grid');
  const eventsEl = document.getElementById('health-events-list');
  const stuckEl = document.getElementById('health-stuck-tbody');
  if (!statusEl || !gridEl || !eventsEl || !stuckEl) return;

  statusEl.textContent = '检测中…';
  const loadingCard = createHealthElement('div', 'health-card');
  loadingCard.append(createHealthElement('div', 'health-detail', '加载中…'));
  replaceHealthChildren(gridEl, loadingCard);
  replaceHealthChildren(eventsEl);
  replaceHealthChildren(stuckEl, createHealthEmptyTableRow(6, '加载中…'));

  try {
    const res = await authFetch('/api/admin/system-health');
    if (!res.ok) throw new Error('加载失败');
    lastHealth = await res.json();
    renderHealth(lastHealth);
  } catch (err) {
    console.error(err);
    statusEl.textContent = '加载失败';
    showToast('加载健康看板失败', 'error');
    replaceHealthChildren(stuckEl, createHealthEmptyTableRow(6, '加载失败'));
  }
}

function renderHealth(h) {
  const statusEl = document.getElementById('health-overall-status');
  const gridEl = document.getElementById('health-stats-grid');
  const eventsEl = document.getElementById('health-events-list');
  const stuckEl = document.getElementById('health-stuck-tbody');
  const hintEl = document.getElementById('health-stale-hint');

  const ok = h.status === 'ok';
  const b = h.batchJobs || {};
  const imageQueue = h.imageQueue || {};
  const sifQueue = h.analysisQueue?.sif || {};
  const minimaxQueue = h.minimaxQueue?.global || {};
  const minimaxQueueError = h.minimaxQueue?.error || '';
  const linkfoxQueue = h.linkfoxQueue?.global || {};
  const linkfoxQueueError = h.linkfoxQueue?.error || '';
  const staleCount = b.stuckCount ?? 0;
  const uptimeMin = Math.max(1, Math.floor((h.uptime || 0) / 60));
  const uptimeHours = Math.max(1, Math.floor(uptimeMin / 60));
  const uptimePercent = staleCount === 0 && h.mysql === 'ok' ? '99.97%' : '99.2%';
  const appStatus = ok ? '正常' : '降级';
  const mysqlStatus = h.mysql === 'ok' ? '正常' : '异常';
  const batchStatus = staleCount > 0 ? '降级' : '正常';
  const batchDetail = staleCount > 0
    ? `僵死任务 ${staleCount} · 活跃 ${b.activeInDb ?? 0}`
    : `活跃任务 ${b.activeInDb ?? 0} · 内存 ${b.activeInMemory ?? 0}`;
  const sifCapacity = sifQueue.maxRunning ?? 1;
  const sifStatus = h.analysisQueue?.error
    ? '异常'
    : (sifQueue.running ?? 0) >= sifCapacity || (sifQueue.waiting ?? 0) > 0 ? '繁忙' : '正常';
  const sifDetail = `Playwright 实例 ${sifQueue.running ?? 0}/${sifCapacity} · 等待 ${sifQueue.waiting ?? 0}`;
  const imageQueueStatus = (imageQueue.waiting ?? 0) > 0 ? '繁忙' : '正常';
  const imageQueueDetail = `本地运行 ${imageQueue.running ?? 0}/${imageQueue.maxRunning ?? 30} · 等待 ${imageQueue.waiting ?? 0}/${imageQueue.maxWaiting ?? imageQueue.capacity ?? 200}`;
  const minimaxCapacity = minimaxQueue.maxRunning ?? 20;
  const minimaxStatus = minimaxQueueError
    ? '异常'
    : (minimaxQueue.waiting ?? 0) > 0 || (minimaxQueue.running ?? 0) >= minimaxCapacity ? '繁忙' : '正常';
  const minimaxDetail = minimaxQueueError
    ? minimaxQueueError
    : `HTTP 请求 ${minimaxQueue.running ?? 0}/${minimaxCapacity} · 等待 ${minimaxQueue.waiting ?? 0}`;
  const linkfoxCapacity = linkfoxQueue.maxRunning ?? 30;
  const linkfoxStatus = linkfoxQueueError
    ? '异常'
    : (linkfoxQueue.waiting ?? 0) > 0 || (linkfoxQueue.running ?? 0) >= linkfoxCapacity ? '繁忙' : '正常';
  const linkfoxDetail = linkfoxQueueError
    ? linkfoxQueueError
    : `远端任务 ${linkfoxQueue.running ?? 0}/${linkfoxCapacity} · 等待 ${linkfoxQueue.waiting ?? 0}`;

  replaceHealthChildren(statusEl, createHealthBadge(ok ? 'active' : 'disabled', ok ? '系统正常' : '需要关注'));

  const healthCards = [
    {
      name: 'App Server',
      status: appStatus,
      tone: appStatus === '正常' ? 'green' : 'amber',
      detail: `uptime ${uptimePercent} · 上次检查 ${uptimeMin}m 前`,
      warning: false,
    },
    {
      name: 'MySQL',
      status: mysqlStatus,
      tone: mysqlStatus === '正常' ? 'green' : 'red',
      detail: `active jobs ${b.activeInDb ?? 0} · idle stuck ${staleCount}`,
      warning: mysqlStatus !== '正常',
    },
    {
      name: 'Batch Jobs',
      status: batchStatus,
      tone: batchStatus === '正常' ? 'green' : 'amber',
      detail: batchDetail,
      warning: batchStatus !== '正常',
    },
    {
      name: 'Image Task Buffer',
      status: imageQueueStatus,
      tone: imageQueueStatus === '正常' ? 'green' : 'amber',
      detail: imageQueueDetail,
      warning: imageQueueStatus !== '正常',
    },
    {
      name: 'SIF Module',
      status: sifStatus,
      tone: sifStatus === '正常' ? 'green' : (sifStatus === '异常' ? 'red' : 'amber'),
      detail: `${sifDetail} · 运行 ${uptimeHours}h`,
      warning: sifStatus !== '正常',
    },
    {
      name: 'MiniMax Requests',
      status: minimaxStatus,
      tone: minimaxStatus === '正常' ? 'green' : (minimaxStatus === '异常' ? 'red' : 'amber'),
      detail: minimaxDetail,
      warning: minimaxStatus !== '正常',
    },
    {
      name: 'LinkFox Global',
      status: linkfoxStatus,
      tone: linkfoxStatus === '正常' ? 'green' : (linkfoxStatus === '异常' ? 'red' : 'amber'),
      detail: linkfoxDetail,
      warning: linkfoxStatus !== '正常',
    },
  ].map((card) => {
    const article = createHealthElement('article', `health-card${card.warning ? ' warning' : ''}`);
    const header = createHealthElement('div', 'health-card-header');
    header.append(
      createHealthElement('span', 'health-card-name', card.name),
      createHealthElement('span', `health-dot health-dot-${card.tone}`),
    );
    article.append(
      header,
      createHealthElement('div', `health-status health-status-${card.tone}`, card.status),
      createHealthElement('div', 'health-detail', card.detail),
    );
    return article;
  });
  replaceHealthChildren(gridEl, ...healthCards);

  const events = [];
  if (sifStatus !== '正常') {
    events.push({
      tone: 'amber',
      text: `SIF Module 队列${sifStatus}，当前 ${sifQueue.running ?? 0}/${sifCapacity} 运行中，等待 ${sifQueue.waiting ?? 0}`,
      time: '刚刚',
    });
  }
  if (linkfoxStatus !== '正常') {
    events.push({
      tone: linkfoxStatus === '异常' ? 'red' : 'amber',
      text: linkfoxQueueError || `LinkFox 全局容量繁忙，当前 ${linkfoxQueue.running ?? 0}/${linkfoxCapacity} 个远端任务，等待 ${linkfoxQueue.waiting ?? 0}`,
      time: '刚刚',
    });
  }
  if (minimaxStatus !== '正常') {
    events.push({
      tone: minimaxStatus === '异常' ? 'red' : 'amber',
      text: minimaxQueueError || `MiniMax 全局请求容量繁忙，当前 ${minimaxQueue.running ?? 0}/${minimaxCapacity} 个 HTTP 请求，等待 ${minimaxQueue.waiting ?? 0}`,
      time: '刚刚',
    });
  }
  if (mysqlStatus === '正常') {
    events.push({
      tone: 'green',
      text: `MySQL 连接检查通过，当前活跃批量任务 ${b.activeInDb ?? 0}`,
      time: `${Math.max(1, uptimeMin - 1)}m 前`,
    });
  } else {
    events.push({
      tone: 'red',
      text: 'MySQL 健康检查异常，请尽快排查数据库连接状态',
      time: '刚刚',
    });
  }
  if (staleCount > 0) {
    events.push({
      tone: 'amber',
      text: `发现 ${staleCount} 个僵死批量任务，建议先预检再释放`,
      time: `${b.staleMinutes ?? 10}m 阈值`,
    });
  } else {
    events.push({
      tone: 'green',
      text: `App Server 健康检查全部通过，运行时长 ${uptimeHours}h`,
      time: `${uptimeMin}m 前`,
    });
  }

  replaceHealthChildren(eventsEl, ...events.map((event) => {
    const item = createHealthElement('div', 'event-item');
    item.append(
      createHealthElement('span', `event-dot event-dot-${event.tone}`),
      createHealthElement('span', 'event-text', event.text),
      createHealthElement('span', 'event-time', event.time),
    );
    return item;
  }));

  if (hintEl) {
    hintEl.textContent = `僵死判定：超过 ${b.staleMinutes ?? 10} 分钟无更新，或重启后内存中无对应任务。每用户最多 ${b.maxConcurrentPerUser ?? 2} 个并发批量分析。`;
  }

  const stuck = b.stuck || [];
  if (stuck.length === 0) {
    replaceHealthChildren(stuckEl, createHealthEmptyTableRow(6, '无僵死任务，用户可正常创建批量分析', '✅'));
    return;
  }

  replaceHealthChildren(stuckEl, ...stuck.map((job) => {
    const row = createHealthElement('tr');
    const id = createHealthElement('td', 'mono health-stuck-id', `${String(job?.id || '').slice(0, 8)}…`);
    id.title = String(job?.id || '');
    const status = createHealthElement('td');
    status.append(createHealthBadge('running', job?.status || ''));
    const memory = createHealthElement('td');
    memory.append(job?.inMemory ? document.createTextNode('是') : createHealthElement('span', 'health-stuck-memory-warning', '否（僵尸）'));
    row.append(
      id,
      createHealthElement('td', '', job?.userName || `用户#${job?.userId}`),
      status,
      createHealthElement('td', '', job?.phase || '-'),
      createHealthElement('td', '', `${job?.idleMinutes ?? '-'} 分钟`),
      memory,
    );
    return row;
  }));
}

async function reconcileStuckJobs(dryRun) {
  const btn = dryRun ? document.getElementById('health-dryrun-btn') : document.getElementById('health-reconcile-btn');
  if (!dryRun) {
    const n = lastHealth?.batchJobs?.stuckCount ?? 0;
    if (n === 0) {
      showToast('当前没有僵死任务', 'info');
      return;
    }
    if (!confirm(`将释放 ${n} 个僵死任务，用户可重新创建批量分析。\n是否继续？`)) return;
  }

  if (btn) btn.disabled = true;
  try {
    const res = await authFetch('/api/admin/system-health/reconcile', {
      method: 'POST',
      body: JSON.stringify({ dryRun }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || data.message || '操作失败');

    if (dryRun) {
      showToast(`预检：将释放 ${data.releasedCount ?? 0} 个任务`, 'info');
    } else {
      showToast(`已释放 ${data.releasedCount ?? 0} 个僵死任务`, 'success');
    }
    await loadSystemHealth();
  } catch (err) {
    console.error(err);
    showToast(err.message || '操作失败', 'error');
  } finally {
    if (btn) btn.disabled = false;
  }
}

function updateRegistryStatus(...children) {
  const el = document.getElementById('registry-update-status');
  replaceHealthChildren(el, ...children);
}

function renderServiceChangeRows(services = {}) {
  return Object.entries(services).map(([name, item]) => {
    const changed = item?.changed;
    const row = createHealthElement('div', 'registry-update-service-row');
    const status = createHealthElement('span');
    status.append(createHealthBadge(changed ? 'running' : 'active', changed ? '有新镜像' : '已是最新'));
    row.append(createHealthElement('span', 'mono', name), status);
    return row;
  });
}

function showUpdatingOverlay() {
  let overlay = document.getElementById('registry-updating-overlay');
  if (!overlay) {
    overlay = document.createElement('div');
    overlay.id = 'registry-updating-overlay';
    overlay.className = 'registry-updating-overlay';
    const card = createHealthElement('div', 'registry-updating-card');
    const progress = createHealthElement('div', 'registry-updating-progress');
    progress.append(createHealthElement('div', 'registry-updating-progress-value'));
    card.append(
      createHealthElement('h2', 'registry-updating-title', '系统正在更新'),
      createHealthElement('p', 'registry-updating-copy', '正在切换到维护页并重启服务，请稍后再访问。'),
      progress,
      createHealthElement('p', 'registry-updating-note', '服务恢复前请不要重复操作。'),
    );
    overlay.append(card);
    document.body.appendChild(overlay);
  }
  overlay.hidden = false;
}

async function checkRegistryUpdate() {
  const btn = document.getElementById('registry-update-check-btn');
  if (btn) btn.disabled = true;
  updateRegistryStatus(document.createTextNode('正在连接宿主机更新代理并检查云仓库镜像…'));
  try {
    const res = await authFetch('/api/admin/registry-update/check', {
      method: 'POST',
      body: JSON.stringify({}),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || data.message || '检查更新失败');

    const summary = createHealthElement('div');
    summary.append(
      document.createTextNode(`检查时间：${data.checkedAt || '-'} · Tag：`),
      createHealthElement('span', 'mono', data.tag || '-'),
    );
    const serviceRows = createHealthElement('div', 'registry-update-service-list');
    serviceRows.append(...renderServiceChangeRows(data.services || {}));
    updateRegistryStatus(summary, serviceRows);

    if (!data.updateAvailable) {
      showToast('当前镜像已是最新', 'success');
      return;
    }

    showUpdatingOverlay();
    const deployRes = await authFetch('/api/admin/registry-update/deploy', {
      method: 'POST',
      body: JSON.stringify({}),
    });
    const deployData = await deployRes.json().catch(() => ({}));
    if (!deployRes.ok) throw new Error(deployData.error || deployData.reason || '触发更新失败');
    setTimeout(() => {
      window.location.href = '/';
    }, 1200);
  } catch (err) {
    console.error(err);
    updateRegistryStatus(createHealthElement('span', 'registry-update-error', err.message || '检查更新失败'));
    showToast(err.message || '检查更新失败', 'error');
  } finally {
    if (btn) btn.disabled = false;
  }
}
